import { Component, Inject, OnInit, ViewChild, forwardRef } from "@angular/core";
import { NgForm } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { isOnline } from "../../common/setup";
import { Alert, Location, Thing, Ticket, TicketItemType, TicketStatus, TicketType } from "../../model";
import { AuthenticationService } from "../../service/authentication.service";
import { ThingContextService } from "../../service/thing-context.service";
import { TicketService } from "../../service/ticket.service";
import { UserLocationService } from "../../service/user-location.service";
import { UserThingService } from "../../service/user-thing.service";
import { CustomPropertyFormComponent } from "../../shared/custom-property/custom-property-form.component";
import { ErrorUtility } from "../../utility/error-utility";
import { EventSelectionInputComponent } from "../event-selection-input/event-selection-input.component";

@Component({
    selector: 'add-ticket-dialog',
    template: require('./add-ticket-dialog.component.html'),
})
export class AddTicketDialogComponent implements OnInit {

    @ViewChild('form') form: NgForm;

    @ViewChild('customPropForm') customPropForm: CustomPropertyFormComponent;

    @ViewChild(EventSelectionInputComponent) eventSelectionInput: EventSelectionInputComponent;

    error: string;
    locations: Location[];
    things: Thing[];
    step2: boolean;
    types: TicketType[];
    isCustomerUser: boolean;
    selectedType: TicketType;
    selectedThing: Thing;
    eventId: string;

    private thingId: string;

    constructor(
        @Inject(forwardRef(() => MatDialogRef)) public dialogRef: MatDialogRef<AddTicketDialogComponent>,
        @Inject(forwardRef(() => UserLocationService)) public userLocationService: UserLocationService,
        @Inject(forwardRef(() => UserThingService)) public userThingService: UserThingService,
        @Inject(forwardRef(() => TicketService)) public ticketService: TicketService,
        @Inject(forwardRef(() => AuthenticationService)) public authenticationService: AuthenticationService,
        @Inject(forwardRef(() => ThingContextService)) public thingContextService: ThingContextService,
        @Inject(MAT_DIALOG_DATA) data
    ) {
        this.thingId = data.thingId;
        this.eventId = data.eventId;
        if (this.thingId) {
            this.step2 = true;
        }
    }

    ngOnInit(): void {
        this.ticketService.getTicketTypes().then(types => {
            if (this.eventId) {
                types = types.filter(t => t.eventSelectionEnabled);
            }
            this.types = types;
            if (this.types.length == 1) {
                this.selectedType = this.types[0];
            }
        });
        this.isCustomerUser = this.authenticationService.isCustomerUser();
        if (this.isCustomerUser && !this.thingId) {
            this.loadLocationList(this.authenticationService.getUser().customerId);
        }
    }

    loadLocationList(customerId: string): void {
        this.things = [];
        if (customerId) {
            this.userLocationService.getRecursivelyAllLocations(null, [], customerId).then(locations => this.locations = locations);
        } else {
            this.locations = [];
        }
    }

    loadThingList(locationId: string): void {
        this.things = [];
        if (locationId) {
            this.userThingService.getRecursivelyAllThings(null, [], locationId).then(things => this.things = things);
        } else {
            this.locations = [];
        }
    }

    goToStep2(): void {
        this.thingId = this.form.form.getRawValue().thingId;
        this.selectedThing = this.things.find(t => t.id == this.thingId);
        this.step2 = true;
    }


    save(): void {
        let body = this.form.form.getRawValue();
        body.thingId = this.thingId;
        body.properties = this.customPropForm?.getProperties();
        const notes = body.notes;
        if (notes) {
            body.items = [{ type: TicketItemType.NOTE, note: { content: notes } }];
            delete body.notes;
        }
        if (this.eventSelectionInput && this.eventSelectionInput.getSelectedEvent()) {
            body.eventId = this.eventSelectionInput.getSelectedEvent().id;
        }
        if (!isOnline()) {
            this.enrichBodyWithOfflineFields(body);
        }
        this.ticketService.save(body).then(ticket => {
            this.dialogRef.close(ticket);
        }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

    private enrichBodyWithOfflineFields(body: Ticket): void {
        const user = this.authenticationService.getUser();
        body.creationUser = user;
        body.creationUserId = user.id;
        if (user.customerId) {
            body.creationCustomerId = user.customerId;
        } else if (user.partnerId) {
            body.creationPartnerId = user.partnerId;
        } else if (user.organizationId) {
            body.creationOrganizationId = user.organizationId;
        }
        let thing = this.thingContextService.getCurrentThing();
        if (!thing) {
            thing = this.things?.find(t => t.id == this.thingId);
        }
        body.thing = thing;
        body.type = this.types.find(t => t.id == body.typeId);
        body.status = TicketStatus.OPEN;
        const now = new Date().getTime();
        body.creationTimestamp = new Date().getTime();
        if (body.items?.length) {
            body.items[0].timestamp = now;
            body.items[0].userId = body.creationUserId;
            body.items[0].user = body.creationUser;
        }
    }

    updateSelectedType(typeId: string): void {
        this.selectedType = this.types.find(t => t.id == typeId);
    }

    isCustomPropertiesFormValid(): boolean {
        if (!this.customPropForm) {
            return true;
        }
        return this.customPropForm.checkForm();
    }

    updateTitle(event: Alert): void {
        const titleControl = this.form?.controls?.title;
        if (event.title && titleControl?.pristine) {
            titleControl.setValue(event.title);
        }
    }

}